import { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Route, Routes, useLocation } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import ProtectedRoute from './features/auth/ProtectedRoute';
import SessionTimeout from './features/auth/SessionTimeout';
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import Loader from './components/ui/Loader';
import 'bootstrap/dist/js/bootstrap.bundle.min.js';

// Lazy load components for code splitting
const Home = lazy(() => import('./features/landing/Home'));
const Features = lazy(() => import('./features/landing/Features'));
const HowItWorks = lazy(() => import('./features/landing/HowItWorks'));
const About = lazy(() => import('./features/landing/About'));
const Contact = lazy(() => import('./features/landing/Contact'));
const PrivacyPolicy = lazy(() => import('./features/legal/PrivacyPolicy'));
const DeleteAccount = lazy(() => import('./features/legal/DeleteAccount'));
const TermsAndConditions = lazy(() => import('./features/legal/TermsAndConditions'));
const SupportPage = lazy(() => import('./features/support/SupportPage'));
const Login = lazy(() => import('./features/auth/Login'));
const Signup = lazy(() => import('./features/auth/Signup'));
const AuthCallback = lazy(() => import('./features/auth/AuthCallback'));
const VerifyOTP = lazy(() => import('./features/auth/VerifyOTP'));
const Dashboard = lazy(() => import('./features/dashboard/Dashboard'));
const CreateOrganization = lazy(() => import('./features/organizations/CreateOrganization'));
const OrganizationCheck = lazy(() => import('./features/organizations/OrganizationCheck'));
const OrganizationsList = lazy(() => import('./features/organizations/OrganizationsList'));
const AcceptInvitation = lazy(() => import('./features/organizations/AcceptInvitation'));
const Assets = lazy(() => import('./features/assets/Assets'));
const AddAssetForm = lazy(() => import('./features/assets/AddAssetForm'));
const EditAssetForm = lazy(() => import('./features/assets/EditAssetForm'));
const AssetDetails = lazy(() => import('./features/assets/AssetDetails'));
const RoomDetails = lazy(() => import('./features/assets/RoomDetails'));
const BedDetails = lazy(() => import('./features/assets/BedDetails'));
const Accounts = lazy(() => import('./features/accounts/Accounts'));
const TenantsList = lazy(() => import('./features/tenants/TenantsList'));
const AddTenantForm = lazy(() => import('./features/tenants/AddTenantForm'));
const EditTenantForm = lazy(() => import('./features/tenants/EditTenantForm'));
const TenantDetails = lazy(() => import('./features/tenants/TenantDetails'));
const CheckinListView = lazy(() => import('./features/checkins/CheckinListView'));
const CheckinDetailView = lazy(() => import('./features/checkins/CheckinDetailView'));
const CheckoutView = lazy(() => import('./features/checkins/CheckoutView'));
const UtilityListView = lazy(() => import('./features/utilities/UtilityListView'));
const SelectUtilityView = lazy(() => import('./features/utilities/SelectUtilityView'));
const AddUtilityContractView = lazy(() => import('./features/utilities/AddUtilityContractView'));
const AddUtilityContractForm = lazy(() => import('./features/utilities/AddUtilityContractForm'));
const UtilityDetailView = lazy(() => import('./features/utilities/UtilityDetailView'));
const UtilityTypeDetailView = lazy(() => import('./features/utilities/UtilityTypeDetailView'));
const PastCheckinsView = lazy(() => import('./features/assets/PastCheckinsView'));
const CashflowSummaryView = lazy(() => import('./features/accounts/CashflowSummaryView'));
const PLStatementView = lazy(() => import('./features/accounts/PLStatementView'));
const CollectionPaymentsView = lazy(() => import('./features/accounts/CollectionPaymentsView'));
const AccountsAnalytics = lazy(() => import('./features/accounts/AccountsAnalytics'));
const RenewalsView = lazy(() => import('./features/contracts/RenewalsView'));
const ContractsListView = lazy(() => import('./features/contracts/ContractsListView'));
const Settings = lazy(() => import('./features/settings/Settings'));
const ReportsView = lazy(() => import('./features/accounts/ReportsView'));


// Routes that belong to the web app (they use their own layout, without the site header and footer)
const APP_ROUTES = [
  '/login', '/signup', '/auth/callback', '/verify-otp', '/organization-check', '/create-organization',
  '/organizations', '/accept-invitation', '/dashboard', '/assets', '/rooms', '/beds', '/accounts',
  '/tenants', '/checkins', '/utilities', '/settings', '/contracts', '/renewals',
];
const isAppRoute = (pathname) => APP_ROUTES.some((route) => pathname === route || pathname.startsWith(`${route}/`));

const AppContent = () => {
  const location = useLocation();
  const isAppPage = isAppRoute(location.pathname);

  return (
    <div className={`d-flex flex-column min-vh-100${isAppPage ? '' : ' site-shell'}`}>
      <SessionTimeout />
      {!isAppPage && <Header />}
      <main className="flex-grow-1">
        <Suspense fallback={<div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}><Loader type="page" size="large" /></div>}>
          <Routes>
            <Route
              path="/"
              element={
                <>
                  <Home />
                  <Features />
                  <HowItWorks />
                  <About />
                  <Contact />
                </>
              }
            />
            <Route path='/support' element={<SupportPage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/delete-account" element={<DeleteAccount />} />
          <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/verify-otp" element={<VerifyOTP />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/organization-check" element={<ProtectedRoute><OrganizationCheck /></ProtectedRoute>} />
          <Route path="/create-organization" element={<ProtectedRoute><CreateOrganization /></ProtectedRoute>} />
          <Route path="/accept-invitation" element={<ProtectedRoute><AcceptInvitation /></ProtectedRoute>} />
          <Route path="/organizations" element={<ProtectedRoute><OrganizationsList /></ProtectedRoute>} />
          <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/assets" element={<ProtectedRoute><Assets /></ProtectedRoute>} />
          <Route path="/assets/add" element={<ProtectedRoute><AddAssetForm /></ProtectedRoute>} />
          <Route path="/assets/edit/:id" element={<ProtectedRoute><EditAssetForm /></ProtectedRoute>} />
          <Route path="/assets/:id" element={<ProtectedRoute><AssetDetails /></ProtectedRoute>} />
          <Route path="/rooms/:roomId" element={<ProtectedRoute><RoomDetails /></ProtectedRoute>} />
          <Route path="/beds/:bedId" element={<ProtectedRoute><BedDetails /></ProtectedRoute>} />
          <Route path="/accounts" element={<ProtectedRoute><Accounts /></ProtectedRoute>} />
          <Route path="/accounts/collection-payments" element={<ProtectedRoute><CollectionPaymentsView /></ProtectedRoute>} />
          <Route path="/tenants" element={<ProtectedRoute><TenantsList /></ProtectedRoute>} />
          <Route path="/tenants/add" element={<ProtectedRoute><AddTenantForm /></ProtectedRoute>} />
          <Route path="/tenants/edit/:tenantId" element={<ProtectedRoute><EditTenantForm /></ProtectedRoute>} />
          <Route path="/tenants/:tenantId" element={<ProtectedRoute><TenantDetails /></ProtectedRoute>} />
          <Route path="/checkins" element={<ProtectedRoute><CheckinListView /></ProtectedRoute>} />
          <Route path="/checkins/:checkinId" element={<ProtectedRoute><CheckinDetailView /></ProtectedRoute>} />
          <Route path="/checkins/:checkinId/checkout" element={<ProtectedRoute><CheckoutView /></ProtectedRoute>} />
          <Route path="/assets/:assetId/utilities" element={<ProtectedRoute><UtilityListView /></ProtectedRoute>} />
          <Route path="/assets/:assetId/utilities/create" element={<ProtectedRoute><AddUtilityContractForm /></ProtectedRoute>} />
          <Route path="/assets/:assetId/utilities/add" element={<ProtectedRoute><SelectUtilityView /></ProtectedRoute>} />
          <Route path="/assets/:assetId/utilities/add/:utilityId" element={<ProtectedRoute><AddUtilityContractView /></ProtectedRoute>} />
          <Route path="/assets/:assetId/utilities/:utilityId" element={<ProtectedRoute><UtilityTypeDetailView /></ProtectedRoute>} />
          <Route path="/utilities/:contractId" element={<ProtectedRoute><UtilityDetailView /></ProtectedRoute>} />
          <Route path="/assets/:assetId/past-checkins" element={<ProtectedRoute><PastCheckinsView /></ProtectedRoute>} />
          <Route path="/accounts/cashflow" element={<ProtectedRoute><CashflowSummaryView /></ProtectedRoute>} />
          <Route path="/accounts/pl-statement" element={<ProtectedRoute><PLStatementView /></ProtectedRoute>} />
          <Route path="/accounts/analytics" element={<ProtectedRoute><AccountsAnalytics /></ProtectedRoute>} />
          <Route path="/renewals" element={<ProtectedRoute><RenewalsView /></ProtectedRoute>} />
          <Route path="/contracts" element={<ProtectedRoute><ContractsListView /></ProtectedRoute>} />
          <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
          <Route path="/accounts/reports" element={<ProtectedRoute><ReportsView /></ProtectedRoute>} />
          </Routes>
        </Suspense>
      </main>
      {!isAppPage && <Footer />}
    </div>
  );
};

const App = () => {
  return (
    <AuthProvider>
      <Router>
        <AppContent />
      </Router>
    </AuthProvider>
  );
};

export default App;